// Print Sellox HTML (receipt / price label) as an ESC/POS raster image through the Windows
// spooler in RAW mode. Works with ANY driver, incl. "Generic / Text Only" (1SISTEMA/a2/Saint PCs),
// and the output is pixel-identical to the normal HTML print on a real thermal driver.
import html2canvas from 'html2canvas';
import { rawDriverWanted, rawPrinterName, forgetRawPrinterName } from './rawDriverFlag';
import { busToast } from './toastBus';

const RAW_LS_WIDTH = 'sellox_raw_width'; // written by rememberRawDriver (80 → 576 dots, 58 → 384 dots)

function dotsWidth() { try { return localStorage.getItem(RAW_LS_WIDTH) === '58' ? 384 : 576; } catch { return 576; } }
export function rasterRawActive() { return rawDriverWanted(); }

// Render a full HTML document → canvas at printer resolution.
// Primary: SVG <foreignObject> (Chromium's real layout engine → pixel-identical to the driver print).
// Fallback: html2canvas (approximate) if the SVG image fails to load.
export async function renderHtmlToCanvas(html, cssWidthPx = 272) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${cssWidthPx}px;height:10px;border:0;visibility:hidden;`;
  document.body.appendChild(iframe);
  try {
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    await new Promise(r => setTimeout(r, 60));
    try { if (doc.fonts?.ready) await doc.fonts.ready; } catch { /* ignore */ }
    await Promise.all([...doc.images].map(img => (img.complete ? null : new Promise(r => { img.onload = img.onerror = r; }))));
    const body = doc.body;
    body.style.margin = '0'; body.style.background = '#fff';
    const width = Math.max(1, body.scrollWidth || body.offsetWidth || cssWidthPx);
    const height = Math.max(1, body.scrollHeight, doc.documentElement.scrollHeight, Math.ceil(body.getBoundingClientRect().height));
    const dots = dotsWidth();
    try {
      // body{} rules must style the wrapper <div> inside the foreignObject
      const styles = [...doc.querySelectorAll('style')].map(s => s.textContent).join('\n').replace(/\bbody\b/g, '.sx-body');
      const tmp = document.implementation.createHTMLDocument('');
      tmp.body.innerHTML = `<div xmlns="http://www.w3.org/1999/xhtml" class="sx-body" style="width:${width}px;min-height:${height}px;background:#fff;margin:0;box-sizing:border-box"><style>${styles}</style>${body.innerHTML}</div>`;
      const xml = new XMLSerializer().serializeToString(tmp.body.firstChild);
      const h = height + 40; // slack; blank rows are trimmed later
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${h}"><foreignObject width="100%" height="100%">${xml}</foreignObject></svg>`;
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('svg')); img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg); });
      const canvas = document.createElement('canvas');
      canvas.width = dots; canvas.height = Math.ceil(h * dots / width);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.getImageData(0, 0, 1, 1); // throws if tainted (external <img>) → fallback
      return canvas;
    } catch (e) {
      iframe.style.height = (height + 20) + 'px';
      return await html2canvas(body, { scale: dots / width, width, height, windowWidth: cssWidthPx, backgroundColor: '#ffffff', logging: false, useCORS: true });
    }
  } finally { iframe.remove(); }
}

// 1-bit threshold → packed rows (MSB first), trailing blank rows trimmed
export function canvasToRaster(canvas, threshold = 160) {
  const w = canvas.width, h = canvas.height;
  const { data } = canvas.getContext('2d').getImageData(0, 0, w, h);
  const bytesPerRow = Math.ceil(w / 8);
  const bits = new Uint8Array(bytesPerRow * h);
  let last = -1;
  for (let y = 0; y < h; y++) {
    let any = false;
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4; const a = data[i + 3] / 255;
      const lum = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) * a + 255 * (1 - a);
      if (lum < threshold) { bits[y * bytesPerRow + (x >> 3)] |= (0x80 >> (x & 7)); any = true; }
    }
    if (any) last = y;
  }
  const height = last + 1;
  return { bits: bits.subarray(0, bytesPerRow * Math.max(1, height)), bytesPerRow, height: Math.max(1, height) };
}

// GS v 0 blocks (≤256 rows each) + feed + cut
export function rasterToEscpos({ bits, bytesPerRow, height }, { cut = true, feed = 3 } = {}) {
  const parts = [Uint8Array.of(0x1b, 0x40), Uint8Array.of(0x1b, 0x61, 0)];
  const CH = 256;
  for (let y = 0; y < height; y += CH) {
    const rows = Math.min(CH, height - y);
    parts.push(Uint8Array.of(0x1d, 0x76, 0x30, 0, bytesPerRow & 0xff, bytesPerRow >> 8, rows & 0xff, rows >> 8));
    parts.push(bits.subarray(y * bytesPerRow, (y + rows) * bytesPerRow));
  }
  parts.push(Uint8Array.of(0x1b, 0x64, feed));
  if (cut) parts.push(Uint8Array.of(0x1d, 0x56, 0x42, 0x00));
  const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  let o = 0; for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}

export async function htmlToEscposRaster(html, opts = {}) {
  const canvas = await renderHtmlToCanvas(html, opts.cssWidthPx || 272);
  return rasterToEscpos(canvasToRaster(canvas), opts);
}

function toB64(u8) {
  let bin = '';
  for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
  return btoa(bin);
}
async function sendRaw(bytes) {
  const b64 = toB64(bytes);
  const printer = rawPrinterName();
  let r = await window.selloxDesktop.printRaw(printer, b64);
  if (r && r.ok === false && printer && /no se pudo abrir impresora/i.test(String(r.error || r.out || ''))) {
    forgetRawPrinterName();
    r = await window.selloxDesktop.printRaw('', b64);
  }
  if (r && r.ok === false) throw new Error(r.error || r.out || 'raw print failed');
}

// Print one HTML document (receipt or a single label) as one raw job (cut at the end)
export async function printHtmlRaster(html, opts = {}) {
  await sendRaw(await htmlToEscposRaster(html, opts));
}

// 🖨️ One door for every 80mm document that is not a sales receipt (kitchen ticket, pre-cuenta, apartado, vale, shift
// report, transfer, cobro…). Generic/Text Only driver → raster image; desktop → silent IPC; browser → caller's own popup.
// Wu 2026-11-02: these documents went to the text-only driver as HTML and came out as bare driver text.
export function thermalDirect() { return rawDriverWanted() || !!(typeof window !== 'undefined' && window.selloxDesktop?.printHTML); }
export async function printThermalDoc(html, printerName) {
  // Super Lucky 2026-11-02: summary ticket came out BLANK — body{margin:0 auto;width:72mm} centred the content in the hidden
  // 800px print window while the page size was measured as 72mm → everything fell outside the page. Pin the body to the left.
  const pin = '<style>body{margin-left:0!important;margin-right:0!important}</style>';
  let clean = String(html).replace(/<script[\s\S]*?<\/script>/gi, '');
  clean = /<\/head>/i.test(clean) ? clean.replace(/<\/head>/i, pin + '</head>') : pin + clean;
  if (rawDriverWanted()) { await printHtmlRaster(clean); return { ok: true }; }
  const r = await window.selloxDesktop.printHTML(clean, printerName || undefined);
  if (r && r.ok === false) throw new Error(r.error || 'print failed');
  return r || { ok: true };
}
// Drop-in for `window.open('', name, spec)` + document.write + print(): on a direct-print PC it collects the HTML and
// prints it through printThermalDoc when the caller closes the document or calls print(); elsewhere a real popup.
export function openThermalWindow(name = '', spec = '') {
  if (!thermalDirect()) return window.open('', name, spec);
  let buf = '', sent = false;
  const send = () => {
    if (sent || !buf.trim()) return; sent = true;
    printThermalDoc(buf).catch((e) => busToast('error', '🖨️ Impresión falló / 打印失败', { description: String(e?.message || e), duration: 6000 }));
  };
  const document = { open() { buf = ''; sent = false; }, write(s) { buf += String(s); }, writeln(s) { buf += String(s) + '\n'; }, close() { setTimeout(send, 60); } };
  return { document, focus() {}, print: send, close() {}, closed: false, onload: null };
}

// Debug hook: returns PNG data URL of the 1-bit image that would be printed
if (typeof window !== 'undefined') {
  window.__selloxRasterPreview = async (html, cssWidthPx) => {
    const canvas = await renderHtmlToCanvas(html, cssWidthPx || 272);
    const { bits, bytesPerRow, height } = canvasToRaster(canvas);
    const out = document.createElement('canvas'); out.width = canvas.width; out.height = height;
    const ctx = out.getContext('2d'); const img = ctx.createImageData(out.width, height);
    for (let y = 0; y < height; y++) for (let x = 0; x < out.width; x++) {
      const on = bits[y * bytesPerRow + (x >> 3)] & (0x80 >> (x & 7)); const i = (y * out.width + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = on ? 0 : 255; img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    return { png: out.toDataURL('image/png'), width: out.width, height, bytes: rasterToEscpos({ bits, bytesPerRow, height }).length, b64: toB64(rasterToEscpos({ bits, bytesPerRow, height })) };
  };
}
